import React, { useState, useMemo } from 'react';
import { 
  Search, Phone, MapPin, Filter, User, 
  Droplets, Briefcase, Navigation, MessageCircle 
} from 'lucide-react';
import { FamilyMember } from '../types';

interface DirectoryProps {
  members: Record<string, FamilyMember>;
  currentUserId: string | null;
  onEdit: (id: string) => void;
  onLocateInTree: (id: string) => void;
}

const Directory: React.FC<DirectoryProps> = ({ members, currentUserId, onEdit, onLocateInTree }) => {
  // All hooks must be called before any early returns
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'blood' | 'city'>('all');
  const [activeFilterValue, setActiveFilterValue] = useState('');

  // --- 1. SEARCH & FILTER ENGINE ---
  const filteredMembers = useMemo(() => {
    if (!members || Object.keys(members).length === 0) return [];
    let list = Object.values(members);

    // A. Text Search (Name, Location, Profession)
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(m => 
        m.name.toLowerCase().includes(lower) || 
        m.location?.toLowerCase().includes(lower) ||
        m.profession?.toLowerCase().includes(lower)
      );
    }

    // B. Category Filters
    if (filterType === 'blood' && activeFilterValue) {
      list = list.filter(m => m.bloodGroup === activeFilterValue);
    }
    if (filterType === 'city' && activeFilterValue) {
      list = list.filter(m => m.location?.includes(activeFilterValue));
    }

    // Sort: Living first, then Alphabetical
    return list.sort((a, b) => {
        if (a.isDeceased !== b.isDeceased) return a.isDeceased ? 1 : -1;
        return a.name.localeCompare(b.name);
    });
  }, [members, searchTerm, filterType, activeFilterValue]);

  // --- 2. EXTRACT FILTER OPTIONS ---
  const bloodOptions = useMemo(() => {
    if (!members || Object.keys(members).length === 0) return [];
    return [...new Set(Object.values(members).map(m => m.bloodGroup).filter(Boolean))].sort();
  }, [members]);
  
  const cityOptions = useMemo(() => {
    if (!members || Object.keys(members).length === 0) return [];
    return [...new Set(Object.values(members).map(m => m.location?.split(',')[0].trim()).filter(Boolean))].sort();
  }, [members]);

  // Safety check - if members are missing, show loading state
  if (!members || Object.keys(members).length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-indigo-100 mx-auto mb-4 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-indigo-300 border-t-indigo-600 animate-spin"></div>
          </div>
          <p className="text-slate-600 font-bold">Loading directory...</p>
        </div>
      </div>
    );
  }

  const handleWhatsApp = (phone: string) => {
     window.open(`https://wa.me/${phone.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <div className="max-w-5xl mx-auto pb-24 pt-6 px-4 space-y-6 w-full overflow-x-hidden">
      
      {/* HEADER & CONTROLS */}
      <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 sticky top-0 z-10">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-4">
           <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
             <User className="w-6 h-6 text-indigo-600" /> Family Directory
           </h2>
           <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
             {filteredMembers.length} Found
           </span>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
           {/* Search Bar */}
           <div className="flex-1 relative">
              <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search name, city, profession..." 
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
           </div>

           {/* Filter Toggles */}
           <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              <button 
                onClick={() => { setFilterType('all'); setActiveFilterValue(''); }}
                className={`px-4 py-3 rounded-xl text-xs font-bold whitespace-nowrap transition-all border ${filterType === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              >
                All
              </button>
              
              <select 
                onChange={(e) => { setFilterType('blood'); setActiveFilterValue(e.target.value); }}
                className={`px-4 py-3 rounded-xl text-xs font-bold border outline-none appearance-none cursor-pointer transition-all ${filterType === 'blood' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                value={filterType === 'blood' ? activeFilterValue : ''}
              >
                <option value="" disabled>🩸 Blood Group</option>
                {bloodOptions.map(b => <option key={b} value={b}>{b}</option>)}
              </select>

              <select 
                onChange={(e) => { setFilterType('city'); setActiveFilterValue(e.target.value); }}
                className={`px-4 py-3 rounded-xl text-xs font-bold border outline-none appearance-none cursor-pointer transition-all ${filterType === 'city' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                value={filterType === 'city' ? activeFilterValue : ''}
              >
                <option value="" disabled>📍 City</option>
                {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
           </div>
        </div>
      </div>

      {/* MEMBER GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMembers.map((member) => (
          <div key={member.id} className={`bg-white rounded-2xl p-5 shadow-sm border transition-all hover:shadow-md ${member.isDeceased ? 'border-gray-200 bg-gray-50 grayscale opacity-80' : 'border-gray-100'}`}>
             
             <div className="flex items-start gap-4">
                <div className="relative">
                   <img 
                     src={member.avatarUrl || `https://ui-avatars.com/api/?name=${member.name}&background=random`} 
                     className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-sm"
                   />
                   {member.isDeceased && <span className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">Late</span>}
                </div>
                
                <div className="flex-1 min-w-0">
                   <div className="flex justify-between items-start">
                      <h3 className="font-bold text-gray-900 truncate">{member.name}</h3>
                      {currentUserId === member.id && <button onClick={() => onEdit(member.id)} className="text-xs text-indigo-600 font-bold hover:underline">Edit</button>}
                   </div>
                   
                   <div className="space-y-1 mt-1">
                      {member.profession && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 truncate">
                           <Briefcase className="w-3 h-3 text-gray-400" /> {member.profession}
                        </p>
                      )}
                      {member.location && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 truncate">
                           <MapPin className="w-3 h-3 text-gray-400" /> {member.location}
                        </p>
                      )}
                      {member.bloodGroup && filterType === 'blood' && (
                        <p className="text-xs text-red-500 font-bold flex items-center gap-1.5">
                           <Droplets className="w-3 h-3" /> Blood: {member.bloodGroup}
                        </p>
                      )}
                   </div>
                </div>
             </div>

             {/* Action Bar */}
             {!member.isDeceased && (
               <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-gray-50">
                  <button 
                    onClick={() => onLocateInTree(member.id)}
                    className="flex flex-col items-center justify-center gap-1 text-[10px] font-bold text-gray-400 hover:text-indigo-600 transition-colors"
                  >
                    <Navigation className="w-4 h-4" /> Locate
                  </button>
                  
                  {member.phoneNumber ? (
                    <>
                      <a 
                        href={`tel:${member.phoneNumber}`}
                        className="flex flex-col items-center justify-center gap-1 text-[10px] font-bold text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <Phone className="w-4 h-4" /> Call
                      </a>
                      <button 
                        onClick={() => handleWhatsApp(member.phoneNumber!)}
                        className="flex flex-col items-center justify-center gap-1 text-[10px] font-bold text-gray-400 hover:text-green-500 transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" /> WhatsApp
                      </button>
                    </>
                  ) : (
                    <div className="col-span-2 flex items-center justify-center text-[10px] text-gray-300 italic">
                       Contact info hidden
                    </div>
                  )}
               </div>
             )}
          </div>
        ))}

          {filteredMembers.length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-400">
              <Filter className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">
               No family members found.<br />
               <span className="text-xs text-slate-500 block mt-2">
                As you add family members and build your tree, your connections will appear here!
               </span>
              </p>
              <button onClick={() => {setSearchTerm(''); setFilterType('all')}} className="text-indigo-600 text-xs font-bold mt-2 hover:underline">Clear Filters</button>
            </div>
          )}
      </div>
    </div>
  );
};

export default Directory;